import React, { useState } from "react";
import { Loader2, AlertCircle, CheckCircle2, KeyRound } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "./card";
import { Button } from "./button";
import { Input } from "./input";
import { Label } from "./label";
import { Alert, AlertDescription } from "./alert";
import { PageHeader } from "./page-header";

const MIN_PASSWORD_LENGTH = 8;

export interface AccountPanelProps {
  /** Signed-in account, shown read-only. */
  email: string;
  /** Change the account password; resolves to an error message, or null on success. */
  onChangePassword: (password: string) => Promise<string | null>;
}

/**
 * Shared "My account" page body (admin + client portals): read-only profile
 * plus a change-password form. Password changes run through the portal's auth
 * hook — email is edited via support, not here.
 */
export function AccountPanel({ email, onChangePassword }: AccountPanelProps) {
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [saving, setSaving] = useState(false);
  const [notice, setNotice] = useState<{ ok: boolean; text: string } | null>(null);

  const save = async (e: React.FormEvent) => {
    e.preventDefault();
    setNotice(null);

    if (password.length < MIN_PASSWORD_LENGTH) {
      setNotice({ ok: false, text: `Password must be at least ${MIN_PASSWORD_LENGTH} characters.` });
      return;
    }
    if (password !== confirm) {
      setNotice({ ok: false, text: "Passwords don't match." });
      return;
    }

    setSaving(true);
    try {
      const error = await onChangePassword(password);
      if (error) {
        setNotice({ ok: false, text: error });
        return;
      }
      setPassword("");
      setConfirm("");
      setNotice({ ok: true, text: "Password updated." });
    } finally {
      setSaving(false);
    }
  };

  return (
    <div>
      <PageHeader title="My account" description="Manage your sign-in details." />

      <div className="grid max-w-xl gap-4">
        <Card>
          <CardHeader>
            <CardTitle className="text-sm">Profile</CardTitle>
            <CardDescription>Contact support to change your email address.</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-1.5">
              <Label htmlFor="account-email">Email</Label>
              <Input id="account-email" type="email" value={email} readOnly disabled />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="text-sm">Password</CardTitle>
            <CardDescription>Choose a new password to use when signing in.</CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={save} className="space-y-3">
              <div className="space-y-1.5">
                <Label htmlFor="new-password">New password</Label>
                <Input
                  id="new-password"
                  type="password"
                  value={password}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => setPassword(e.target.value)}
                  minLength={MIN_PASSWORD_LENGTH}
                  autoComplete="new-password"
                  placeholder="••••••••"
                />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="confirm-password">Confirm password</Label>
                <Input
                  id="confirm-password"
                  type="password"
                  value={confirm}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => setConfirm(e.target.value)}
                  minLength={MIN_PASSWORD_LENGTH}
                  autoComplete="new-password"
                  placeholder="••••••••"
                />
              </div>

              {notice && (
                <Alert variant={notice.ok ? "default" : "destructive"}>
                  {notice.ok ? <CheckCircle2 className="h-4 w-4" /> : <AlertCircle className="h-4 w-4" />}
                  <AlertDescription>{notice.text}</AlertDescription>
                </Alert>
              )}

              <Button type="submit" disabled={saving || !password || !confirm}>
                {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <KeyRound className="h-4 w-4" />}
                Update password
              </Button>
            </form>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
